/**
 * @file Per-app configuration for Google Search Console (GSC).
 * Collects the user's selected site URLs, then patches `custom_data` on the
 * credential. Renders either as a post-OAuth modal (`variant="dialog"`, the
 * default) or inline inside the Settings sub-tab (`variant="inline"`), where it
 * pre-loads the saved selection.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Loader2 } from "lucide-react";
import { ANALYTICS_EVENTS, useTrack } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  MultiSelect,
  toast,
  cn,
} from "@strivelabs/ui-core";
import { executeAppAction, BASE_URL } from "@/services/DataService";
import { useCredentials, usePatchCredentialCustomData } from "@/hooks/useConnections";
import { logger } from "@/lib/logger";
import { InlineConfigPanel } from "@/components/settings/InlineConfigPanel";
import type { ConnectionsCardData, PostConnectionConfig } from "@/types/connections";

interface GoogleSearchConsoleConfigDialogProps {
  /** Modal open state — used by the dialog variant only. */
  open?: boolean;
  config: PostConnectionConfig;
  onComplete: () => void;
  /** 'dialog' (default) renders a modal; 'inline' renders an embedded panel. */
  variant?: "dialog" | "inline";
}

interface GSCSite {
  url: string;
  permissionLevel: string;
}

/** Shape of a single site entry from the GSC list_sites response. */
interface GSCSiteEntryRaw {
  siteUrl: string;
  permissionLevel: string;
}

/** Shape of the executeAppAction response. */
interface ActionResponse<T> {
  status: string;
  items: Array<{
    scenario: string;
    data: T;
  }>;
}

function parseSitesResponse(res: unknown): GSCSite[] {
  const response = res as ActionResponse<{ response_data: { siteEntry: GSCSiteEntryRaw[] } }>;
  const siteEntries = response?.items?.[0]?.data?.response_data?.siteEntry ?? [];

  return siteEntries.map((entry) => ({
    url: entry.siteUrl,
    permissionLevel: entry.permissionLevel,
  }));
}

/** Previously-saved site URLs read from the credential's custom_data. */
/** Order-independent set equality for id lists. */
function sameIdSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

function savedSiteUrls(cred: ConnectionsCardData | undefined): string[] {
  const raw = (cred?.custom_data?.site_urls ?? []) as Array<{ url?: string }>;
  return raw.map((s) => s?.url).filter((u): u is string => typeof u === "string");
}

export function GoogleSearchConsoleConfigDialog({
  open,
  config,
  onComplete,
  variant = "dialog",
}: GoogleSearchConsoleConfigDialogProps) {
  const isInline = variant === "inline";
  const trackEvent = useTrack();
  // The form is "active" (fetches data) whenever the modal is open, or always
  // for the inline panel (which only mounts when its sub-tab is shown).
  const active = isInline || !!open;

  const [sites, setSites] = useState<GSCSite[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patchCustomData = usePatchCredentialCustomData();
  const { data: credentialsData, isSuccess: credentialsLoaded } = useCredentials();

  /** The currently-saved selection — used to pre-load the inline panel. */
  const savedUrls = useMemo(() => {
    const cred = (credentialsData?.credentials ?? []).find(
      (c: ConnectionsCardData) => c.id === config.credentialId
    );
    return savedSiteUrls(cred);
  }, [credentialsData, config.credentialId]);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await executeAppAction(
        BASE_URL,
        "google_search_console",
        "GOOGLE_SEARCH_CONSOLE_LIST_SITES",
        [{ id: config.credentialId }]
      );
      const parsed = parseSitesResponse(res);
      if (parsed.length === 0) {
        setError("No sites found. Please check your Search Console access.");
      }
      setSites(parsed);
    } catch (err) {
      logger(err as Error, { context: "GSC config: Error loading sites" });
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('insufficient') || message.includes('scope') || message.includes('403') || message.includes('401')) {
        setError("Insufficient permissions. Please re-authorize your Google Search Console account and try again. If the issue persists, contact support.");
      } else {
        setError("Failed to load sites. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [config.credentialId]);

  // Load the site list whenever the surface becomes active. The dialog clears
  // the selection on (re)open; the inline panel keeps its pre-loaded selection.
  useEffect(() => {
    if (!active) return;
    if (!isInline) setSelectedUrls([]);
    fetchSites();
  }, [active, isInline, fetchSites]);

  // Inline: pre-load the saved selection once the credential data is available.
  const initedRef = useRef(false);
  useEffect(() => {
    if (!isInline || initedRef.current || !credentialsLoaded) return;
    initedRef.current = true;
    setSelectedUrls(savedUrls);
  }, [isInline, credentialsLoaded, savedUrls]);

  const handleSave = async () => {
    const selectedSites = sites
      .filter((s) => selectedUrls.includes(s.url))
      .map((s) => ({ url: s.url }));

    try {
      await patchCustomData.mutateAsync({
        credentialId: config.credentialId,
        customData: {
          site_urls: selectedSites,
        },
      });

      toast({
        title: "Configuration saved",
        description: `${config.displayName} has been configured with ${selectedSites.length} ${selectedSites.length === 1 ? "site" : "sites"}.`,
      });
      trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONFIGURED, {
        provider: config.appName,
        config_type: "app_config",
      });
      onComplete();
    } catch (err) {
      logger(err as Error, { context: "GSC config: Error saving custom data" });
      toast({
        title: "Error",
        description: "Failed to save configuration. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Require a selection AND a change from what's saved — so Save is disabled
  // when nothing is picked, and after a save (selection == saved).
  const canSave = selectedUrls.length > 0 && !sameIdSet(selectedUrls, savedUrls);
  const siteOptions = sites.map((s) => ({ label: s.url, value: s.url }));

  // Mandatory selection: the post-connect dialog must not be dismissed without
  // choosing a site when the account has verified properties and none are saved
  // yet — otherwise the credential is created with no site_urls and the datasync
  // silently never runs. When there are no sites to pick (empty/error) the user
  // can still exit, since there's nothing to select.
  const mustSelect = !isInline && sites.length > 0 && savedUrls.length === 0;

  const body = (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Sites</Label>
      {loading ? (
        <div className="flex items-center gap-2 py-4 justify-center text-sm text-custom-text-primary opacity-50">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sites...
        </div>
      ) : error ? (
        <div className="space-y-2 py-2">
          <p className="text-sm text-red-500">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchSites}>
            Retry
          </Button>
        </div>
      ) : (
        <MultiSelect
          options={siteOptions}
          defaultValue={[]}
          value={selectedUrls}
          onValueChange={setSelectedUrls}
          placeholder="Select sites"
          disablePortal
        />
      )}
    </div>
  );

  if (isInline) {
    return (
      <InlineConfigPanel
        title={`Configure ${config.displayName}`}
        description="Select the sites you want to sync data from."
        onSave={handleSave}
        canSave={canSave}
        saving={patchCustomData.isPending}
      >
        {body}
      </InlineConfigPanel>
    );
  }

  return (
    <Dialog
      open={!!open}
      onOpenChange={(isOpen) => {
        // Non-dismissible while a selection is required — only complete on close
        // when the user isn't forced to pick a site.
        if (!isOpen && !mustSelect) onComplete();
      }}
    >
      <DialogContent
        // Hide the close (X) and block Esc / click-outside while a selection is
        // required, so the only way forward is to select a site and Save.
        className={cn("sm:max-w-lg", mustSelect && "[&>button]:hidden")}
        onEscapeKeyDown={(e) => { if (mustSelect) e.preventDefault(); }}
        onInteractOutside={(e) => { if (mustSelect) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Configure {config.displayName}</DialogTitle>
          <DialogDescription>
            Select the sites you want to sync data from.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">{body}</div>

        <DialogFooter className="gap-2 sm:gap-0">
          {mustSelect ? (
            <p className="mr-auto self-center text-xs text-custom-text-primary opacity-60">
              Select at least one site to finish connecting.
            </p>
          ) : (
            <Button variant="outline" onClick={onComplete}>
              Skip
            </Button>
          )}
          <Button
            variant="black"
            onClick={handleSave}
            disabled={!canSave || patchCustomData.isPending}
          >
            {patchCustomData.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
